import React from 'react';

import { FormPanel } from '@checkit/react-components';

import {
  EntitySnapshotWithType,
  EntityType
} from '../state';

/** Arguments to the callback function which renders an entity's autocomplete. */
export interface RenderEntityAutocompleteContext<TEntityType extends EntityType> {

  /** Adds the given entity to the selected entities (if it isn't already selected). */
  handleAdd: (toAdd: EntitySnapshotWithType<TEntityType>) => void;

}
/** Arguments to the callback function which renders an entity's table of selected items. */
export interface RenderEntityTableContext<TEntityType extends EntityType> {

  /** Removes the entity corresponding to the given snapshot from the library's selected entities (if present). */
  handleDelete: (snapshot: EntitySnapshotWithType<TEntityType>) => void;

  /** Updates the given entity snapshot to the given replacement. */
  handleUpdate: (
    snapshot: EntitySnapshotWithType<TEntityType>,
    replacement: EntitySnapshotWithType<TEntityType>
  ) => void;

  /** Update the given entity snapshot to the previous version. */
  handleUndo: (
    snapshot: EntitySnapshotWithType<TEntityType>,
    replacement: EntitySnapshotWithType<TEntityType>
  ) => void;

}

/**
 * An expandable form panel for selecting entities for inclusion in a library.
 * Encapsulates and abstracts the state logic for adding/removing an entity from the list
 * of selected entities, so it can be re-used for different entity types.
 */
export function SelectedEntityPanel<TEntityType extends EntityType = EntityType>({
  heading,
  selected,
  setSelected,
  isLibrarySaved,
  type,
  // child components
  Autocomplete,
  Table
}: {
  /** The key of the entity type being selected. */
  type: string;
  /** The entities which have been selected for inclusion. */
  selected: Array<EntitySnapshotWithType<TEntityType>>;
  /** A callback used to signal the fact that the selected entities have changed. */
  setSelected: (selection: Array<EntitySnapshotWithType<TEntityType>>) => void;
  /** Whether the library these entities are in has actually been saved to the server. */
  isLibrarySaved: boolean;
  /** The panel's heading displayed to the user. */
  heading: string;
  /** The autocomplete component used for selecting new entities. */
  Autocomplete: (context: RenderEntityAutocompleteContext<TEntityType>) => JSX.Element;
  /** The table component which displays the already selected entities. */
  Table: (context: RenderEntityTableContext<TEntityType>) => JSX.Element;
}) {
  // whether the panel should initially be expanded when the form is first viewed.
  // if the library is in the process of being created, then always show the panel so that
  // the user can immediately see the action to be taken. otherwise, only show the panel
  // if it actually has contents.
  const isExpanded = !isLibrarySaved || selected.length > 0;

  function handleDelete(toDelete: EntitySnapshotWithType<TEntityType>) {
    const deletedCheck = selected.filter((entity) => entity.id !== toDelete.id);
    setSelected(deletedCheck);
  }

  function handleUndo(
    toUndo: EntitySnapshotWithType<TEntityType>,
    previous: EntitySnapshotWithType<TEntityType>
  ) {
    if (toUndo.code === previous.code) {
      // no-op, avoid invalidating state
      return;
    }

    if (toUndo.id !== previous.id) {
      throw new Error(`Attempted to undo snapshot for entity ${toUndo.id} to different entity ${previous.id}`);
    }
    const indexToRestore = selected.indexOf(toUndo);
    if (indexToRestore === -1) {
      throw new Error(`Attempted to undo snapshot ${toUndo.code}, which isn't selected`);
    }

    setSelected([
      ...selected.slice(0, indexToRestore),
      previous,
      ...selected.slice(indexToRestore + 1)
    ]);
  }

  function handleUpdate(
    toUpdate: EntitySnapshotWithType<TEntityType>,
    updated: EntitySnapshotWithType<TEntityType>
  ) {
    if (toUpdate.code === updated.code) {
      // no-op, avoid invalidating state
      return;
    }

    if (toUpdate.id !== updated.id) {
      throw new Error(`Attempted to update snapshot for entity ${toUpdate.id} to different entity ${updated.id}`);
    }

    const indexToUpdate = selected.indexOf(toUpdate);
    if (indexToUpdate === -1) {
      throw new Error(`Attempted to update snapshot ${toUpdate.code} which isn't selected`);
    }

    setSelected([
      ...selected.slice(0, indexToUpdate),
      updated,
      ...selected.slice(indexToUpdate + 1)
    ]);
  }

  function handleAdd(toAdd: EntitySnapshotWithType<TEntityType>) {
    // check if there's already a snapshot for that entity, and if so update it instead
    const existing = selected.find((entity) => entity.id === toAdd.id);
    if (existing != null) {
      handleUpdate(existing, toAdd);
    } else {
      setSelected([ ...selected, toAdd ]);
    }
  }

  return (
    <FormPanel heading={heading} name={`library-selected-${type}`} initialExpanded={isExpanded}>
      <Autocomplete handleAdd={handleAdd} />
      <Table handleDelete={handleDelete} handleUpdate={handleUpdate} handleUndo={handleUndo} />
    </FormPanel>
  );
}
